import { Component, Inject, OnInit } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { StammkundeService } from '../../../../Services/stammkunde.service';
import { TakedaExportEinsatzOption } from '../../../../Models/stammkunde.model';
import { speichereTakedaExportDatei, takedaExportFehlerText } from '../takeda-export-download.util';

export interface TakedaExcelExportDialogData {
  auftragId: number;
  auftragTitel?: string | null;
}

/**
 * Exportvariante: „Excel Gesamt“ = ein Blatt „Gesamtauftrag“ mit allen Stunden und
 * Materialien des Auftrags; „Excel nach Einsatz“ = ein Blatt je Einsatz (RA0001, …).
 */
export type TakedaExcelExportModus = 'gesamt' | 'einsaetze';

/**
 * Exportdialog der Takeda-Regieabrechnung.
 *
 * Der Download läuft hier im Dialog: so sieht der Benutzer den Ladezustand an
 * der Stelle, an der er geklickt hat, Fehler erscheinen im Dialog, und ein
 * zweiter Klick während des Exports ist ausgeschlossen. Nach erfolgreichem
 * Download schließt sich der Dialog mit `true`.
 *
 * Die Einsätze werden vom Server geladen – aus derselben Abrechnung, die auch
 * exportiert wird. Sie dienen hier nur dazu, „Excel nach Einsatz“ bei einem
 * Auftrag ohne Einsätze zu sperren und das zu erklären.
 */
@Component({
  selector: 'app-takeda-excel-export-dialog',
  templateUrl: './takeda-excel-export-dialog.component.html',
  styleUrls: ['./takeda-excel-export-dialog.component.css', '../../stammkunden-theme.css']
})
export class TakedaExcelExportDialogComponent implements OnInit {
  modus: TakedaExcelExportModus = 'gesamt';

  einsaetze: TakedaExportEinsatzOption[] = [];
  einsaetzeLaden = true;
  einsaetzeFehler: string | null = null;

  exportLaeuft = false;
  fehler: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<TakedaExcelExportDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: TakedaExcelExportDialogData,
    private stammkundeService: StammkundeService
  ) {}

  ngOnInit(): void {
    this.stammkundeService.getTakedaExportEinsaetze(this.data.auftragId).subscribe({
      next: einsaetze => {
        this.einsaetze = einsaetze ?? [];
        this.einsaetzeLaden = false;
      },
      error: err => {
        this.einsaetze = [];
        this.einsaetzeLaden = false;
        this.einsaetzeFehler = takedaExportFehlerText(err, 'Die Einsätze konnten nicht geladen werden.');
      }
    });
  }

  get keineEinsaetze(): boolean {
    return !this.einsaetzeLaden && !this.einsaetzeFehler && this.einsaetze.length === 0;
  }

  /** Exportbutton nur, wenn die gewählte Variante etwas zu exportieren hat. */
  get kannExportieren(): boolean {
    if (this.exportLaeuft) { return false; }
    if (this.modus === 'gesamt') { return true; }
    return !this.einsaetzeLaden && this.einsaetze.length > 0;
  }

  auswahlGeaendert(): void {
    this.fehler = null;
  }

  exportieren(): void {
    if (!this.kannExportieren) { return; }

    this.exportLaeuft = true;
    this.fehler = null;
    this.dialogRef.disableClose = true;

    this.anfrage().subscribe({
      next: response => {
        const gespeichert = speichereTakedaExportDatei(response, 'Takeda-Abrechnung.xlsx');
        this.exportBeendet();

        if (!gespeichert) {
          this.fehler = 'Die Excel-Datei war leer.';
          return;
        }

        this.dialogRef.close(true);
      },
      error: err => {
        this.exportBeendet();
        this.fehler = takedaExportFehlerText(err, 'Die Excel-Datei konnte nicht erzeugt werden.');
      }
    });
  }

  abbrechen(): void {
    if (this.exportLaeuft) { return; }
    this.dialogRef.close(false);
  }

  private anfrage(): Observable<HttpResponse<Blob>> {
    const id = this.data.auftragId;
    return this.modus === 'gesamt'
      ? this.stammkundeService.downloadTakedaExcel(id)
      : this.stammkundeService.downloadTakedaExcelAlleEinsaetze(id);
  }

  private exportBeendet(): void {
    this.exportLaeuft = false;
    this.dialogRef.disableClose = false;
  }
}
